import React, { useContext } from 'react';

import UIContext from 'components/Utils/UIContext';

import { faClone, faClose, faEdit, faEyeSlash, faTrash } from "@fortawesome/pro-light-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import ActionButton from "components/action/ActionButton";

import { useTranslation } from "react-i18next";

import "./FishboneItemGeneralTab.sass";
import { Button, Flex, Tag, Tooltip, Slider } from 'antd';
import { sortBy } from 'lodash';
import Weight from 'components/controls/Weight';
import { IconPicker } from 'components/IconPicker';

import dayjs from 'dayjs';
import { MiniSlider } from 'components/controls/MiniSlider';
import { FishboneGeneralLinks } from './FishboneGeneralLinks';

interface FishboneItemGeneralTabProps {
    selection: any;
    onEdit: Function;
    onClone: Function;
    onClose: Function;
    onDisable: Function;
    onDelete: Function;
    onChange: Function;
    scenario: any;

    risks: any[];
    fishboneBranchs: any[];
    onAddItem?: Function;

    stakes: any[];
    indicators: any[];
    stakeholders: any[];
    select: Function;
    createStakeImpact: Function;
    deleteStakeImpact: Function;
    updateImpactStrength: Function;

}

export const FishboneItemGeneralTab = (props: FishboneItemGeneralTabProps) => {

    const { risks = [] } = props;
    const { t } = useTranslation();
    const uiContext = useContext(UIContext);

    const realRisks = risks.filter(r => !r.isOpportunity);
    const realOpportunities = risks.filter(r => r.isOpportunity);

    return (
        <div className="FishboneItemGeneralTab noselection">

            <Flex vertical>

                <Flex className="mb-1" align="center">

                    <div
                        className="IconPicker"
                        style={{ marginRight: '1rem' }}
                    >
                        <IconPicker
                            value={props.selection?.icon || 'faCrosshairs'}
                            disabled={!!props.scenario || !uiContext.canEdit}
                            onChange={
                                props.scenario || !uiContext.canEdit
                                    ? () => { }
                                    : (v) => props.onChange(props.selection.id, { icon: v })
                            }
                        />
                    </div>

                    <div className="w-100 text-left">
                        <div className="title">{props.selection?.name}</div>
                        <small>{t("models.fishbone_item.name")}</small>
                    </div>




                    <Tooltip title={t('actions.close')} placement='left'>
                        <Button
                            shape="circle"
                            variant="solid"
                            className="mini ml-auto"
                            tooltip={t('models.item.actions.edit')}
                            style={{ marginRight: '0.4rem' }}
                            onClick={props.onClose}
                        >
                            <FontAwesomeIcon icon={faClose} />
                        </Button></Tooltip>
                </Flex>

                {props.selection?.description && (
                    <div className="fishbone-description">{props.selection?.description}</div>
                )}

                <ul>
                    {props.selection?.responsible && <li className="EditableListItem">
                        <div style={{ width: '45%', textAlign: 'left' }}>
                            {t("models.fishbone_branch.fields.responsible")}
                        </div>
                        <div>
                            {props.selection?.responsible}
                        </div>
                    </li>}

                    <li className="EditableListItem">
                        <div style={{ width: '45%', textAlign: 'left' }}>
                            {t("models.fishbone_branch.fields.completion")}
                        </div>
                        <div>
                            <Slider
                                value={props.selection?.completion}
                                key={props.selection?.id}
                                min={0}
                                max={100}
                                step={10}
                                disabled={!uiContext.canEdit}
                                style={{ width: '145px' }}
                                onChange={(value: any) =>
                                    props.onChange(props.selection.id, { completion: value })
                                }
                            />
                        </div>
                    </li>

                    <li className="EditableListItem">
                        <div style={{ width: '45%', textAlign: 'left' }}>
                            {t("models.fishbone_branch.fields.trend")}
                        </div>
                        <div>
                            <MiniSlider
                                value={props.selection?.trend}
                                key={props.selection?.id}
                                min={0}
                                max={100}
                                readOnly={!uiContext.canEdit}
                                onChange={(value: any) =>
                                    props.onChange(props.selection.id, { trend: '' + value })
                                }
                                className="highContrast"
                                style={{ width: '150px' }}
                            ></MiniSlider>
                        </div>
                    </li>

                    <li className="EditableListItem">
                        <div style={{ width: '45%', textAlign: 'left' }}>
                            {t("models.fishbone_branch.fields.weight")}
                        </div>
                        <div>
                            <Weight disabled={!uiContext.canEdit} onClick={() => props.onChange(props.selection?.id, { weight: props.selection?.weight > 2 ? 1 : props.selection?.weight + 1 })}>{props.selection?.weight}</Weight>
                        </div>
                    </li>

                    <li className="EditableListItem">
                        <div style={{ width: '45%', textAlign: 'left' }}>
                            {t("models.fishbone_branch.fields.dates")}
                        </div>
                        <div>
                            {
                                <small>
                                    {props.selection?.startOn ? dayjs(
                                        props.selection?.startOn
                                    ).format('DD/MM/YYYY') : t("messages.not_defined")}
                                </small>
                            }
                            {props.selection?.endOn &&
                                !props.selection?.startOn ?
                                'Until ' : props.selection?.endOn ? <small className="mr-05 ml-05">-</small> : null}
                            {props.selection?.endOn && (
                                <small>
                                    {dayjs(props.selection?.endOn).format(
                                        'DD/MM/YYYY'
                                    )}
                                </small>
                            )}
                        </div>
                    </li>
                </ul>

                <div className="Actions mt-2 mb-4 text-center" data-testid="fishbone-item-actions">

                    {props.onEdit && <ActionButton
                        className="small"
                        data-testid="action-edit"
                        tooltip={t('models.fishbone_item.actions.edit')}
                        onClick={() => props.onEdit(props.selection.id)}
                    >
                        <FontAwesomeIcon icon={faEdit} />
                    </ActionButton>}

                    {props.onClone && <ActionButton
                        className="small"
                        data-testid="action-clone"
                        tooltip={t('models.fishbone_item.actions.clone')}
                        onClick={() => props.onClone(props.selection)}
                    >
                        <FontAwesomeIcon icon={faClone} />
                    </ActionButton>}

                    {props.onDisable && <ActionButton
                        className="small"
                        data-testid="action-disable"
                        tooltip={t('actions.disable')}
                        onClick={() => props.onDisable(props.selection.id)}
                    >
                        <FontAwesomeIcon icon={faEyeSlash} />
                    </ActionButton>}

                    {props.onDelete && <ActionButton
                        className="small"
                        data-testid="action-delete"
                        tooltip={t('models.fishbone_item.actions.delete')}
                        onClick={() => props.onDelete(props.selection.id)}
                    >
                        <FontAwesomeIcon icon={faTrash} />
                    </ActionButton>}
                </div>

                <FishboneGeneralLinks
                    selection={props.selection}
                    nodeType="item"
                    scenario={props.scenario}
                    select={props.select}
                    risks={props.risks}
                    stakes={props.stakes}
                    indicators={props.indicators}
                />

                {false && (
                    <div className="d-flex flex-column flex-grow mt-3">
                        <div className="text-left mb-05">
                            {t('models.stake.fields.risks.name')}
                        </div>
                        <div className="text-left">
                            {realRisks && realRisks.length ? (
                                sortBy(realRisks, 'name').map(
                                    (r: any, index) => {
                                        const impactedStake = r.impactedStakes.find(ii => ii.stakeId == props.selection?.id);
                                        return (
                                            <Tag.CheckableTag
                                                key={index}
                                                checked={impactedStake?.id}
                                                onClick={async (e) => {

                                                    e.stopPropagation();
                                                    e.preventDefault();
                                                    if (
                                                        !impactedStake ||
                                                        !impactedStake.id
                                                    ) {
                                                        props.createStakeImpact(
                                                            r.id,
                                                            props.selection?.id,

                                                        );
                                                    } else if (
                                                        impactedStake.strength <
                                                        3
                                                    ) {
                                                        props.updateImpactStrength(
                                                            impactedStake,
                                                            impactedStake.strength +
                                                            1
                                                        );
                                                    } else {
                                                        props.deleteStakeImpact(
                                                            impactedStake.id,
                                                            r.id
                                                        );
                                                    }
                                                }}
                                                className={[
                                                    'ImpactedStake tag'
                                                ].join(' ')}
                                            >

                                                <span className="tag__label" title={r.name}>{r.name}</span>


                                            </Tag.CheckableTag>
                                        );
                                    }
                                )
                            ) : (
                                <div
                                    className="text-center"
                                    style={{ color: 'var(--ant-color-text-tertiary)' }}
                                >
                                    {t('models.stake.no_x')}
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {false && (
                    <div className="d-flex flex-column flex-grow mt-4 mb-4">
                        <div className="text-left mb-05">
                            {t('models.stake.fields.risks.name_positive')}
                        </div>
                        <div className="text-left">
                            {realOpportunities && realOpportunities.length ? (
                                sortBy(realOpportunities, 'name').map(
                                    (r: any, index) => {
                                        const impactedStake = r.impactedStakes.find(ii => ii.stakeId == props.selection?.id);

                                        return (
                                            <Tag.CheckableTag
                                                key={index}
                                                checked={impactedStake?.id}
                                                onClick={async (e) => {

                                                    e.stopPropagation();
                                                    e.preventDefault();
                                                    if (
                                                        !impactedStake ||
                                                        !impactedStake.id
                                                    ) {
                                                        props.createStakeImpact(
                                                            r.id,
                                                            props.selection?.id,

                                                        );
                                                    } else if (
                                                        impactedStake.strength <
                                                        3
                                                    ) {
                                                        props.updateImpactStrength(
                                                            impactedStake,
                                                            impactedStake.strength +
                                                            1
                                                        );
                                                    } else {
                                                        props.deleteStakeImpact(
                                                            impactedStake.id,
                                                            r.id
                                                        );
                                                    }
                                                }}
                                                className={[
                                                    'ImpactedStake tag'
                                                ].join(' ')}
                                            >

                                                <span className="tag__label" title={r.name}>{r.name}</span>


                                            </Tag.CheckableTag>
                                        );
                                    }
                                )
                            ) : (
                                <div
                                    className="text-center"
                                    style={{ color: 'var(--ant-color-text-tertiary)' }}
                                >
                                    {t('models.stake.no_x')}
                                </div>
                            )}
                        </div>
                    </div>
                )}


            </Flex>
        </div >
    );
}